import {
  BehaviorSubject,
  catchError,
  delay,
  filter,
  from,
  mergeMap,
  Observable,
  of,
} from 'rxjs';
import { Dependencies } from '../../../../configurations/dependencies/dependencies.interface';
import { Action, AnyAction } from '../../../state/action.type';
import { AppState } from '../../../state/app.state';
import {
  googleSignIn,
  signInFailure,
  signInSuccess,
  verifyToken,
} from '../../../state/authentication/authentication.action';
import {
  GoogleSignIn,
  GOOGLE_SIGN_IN,
} from '../../../state/authentication/authentication.action.type';
import {
  createUserProfile,
  getUserProfile,
} from '../../../state/user/user.action';
import { AUTH_USECASE_ERROR_MESSAGES } from '../../errors/usecases.errorMessages/authenticationUsecase.error.messages';
import { GoogleAuthResponse } from '../../models/authenticator.model';
import { SignUpProfile } from '../../models/user.model';

const DEFAULT_RETRY_DELAY = 300;

export const googleSignInUsecase = (
  action$: Observable<Action>,
  _state$: BehaviorSubject<AppState>,
  dependencies: Dependencies,
): Observable<Action> =>
  action$.pipe(
    filter(
      (action: AnyAction): action is GoogleSignIn =>
        action.type === GOOGLE_SIGN_IN,
    ),
    mergeMap((action: GoogleSignIn) =>
      from(dependencies.oicdAuthenticatorGateway.googleSignIn()).pipe(
        mergeMap((response: GoogleAuthResponse) => {
          if (response.isNewUser) {
            const names = response.displayName?.split(' ') || [];
            const userData: SignUpProfile = {
              firstName: names[0] || '',
              lastName: names.slice(1).join(' ') || '',
              email: response.email,
              nationality: undefined,
              dateOfBirth: undefined,
              gender: undefined,
            };
            return [
              verifyToken({ token: response.token, retry: 2 }),
              signInSuccess(response.token),
              createUserProfile({
                token: response.token,
                profile: userData,
                retry: 0,
              }),
            ];
          }
          return [
            verifyToken({ token: response.token, retry: 2 }),
            signInSuccess(response.token),
            getUserProfile({
              token: response.token,
              retry: 2,
            }),
          ];
        }),
        catchError((error: Error) => {
          console.warn('Google Sign In Failed:', error.message);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const remainingRetries = action?.payload?.retry
            ? action.payload.retry - 1
            : 0;
          if (remainingRetries) {
            return of(googleSignIn({ retry: remainingRetries })).pipe(
              delay(DEFAULT_RETRY_DELAY),
            );
          }
          return of(
            signInFailure(AUTH_USECASE_ERROR_MESSAGES.GOOGLE_SIGN_IN_FAILED),
          );
        }),
      ),
    ),
  );
